import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";

/**
 * Radix dialog informing users about the domain migration to kisekidle.app.
 * Provides step-by-step instructions for exporting/importing game data.
 *
 * @param {{ isOpen: boolean, onClose: () => void, currentTheme: string, permanent?: boolean, onOpenSettings?: () => void }} props
 * @param {boolean} props.isOpen - Whether the dialog is visible.
 * @param {() => void} props.onClose - Callback to dismiss the dialog.
 * @param {string} props.currentTheme - Active era theme name.
 * @param {boolean} [props.permanent] - If true, modal is non-dismissable (old domain).
 * @param {() => void} [props.onOpenSettings] - Opens the settings modal for backup.
 * @returns {JSX.Element} The migration modal dialog.
 */
export default function MigrationModal({ isOpen, onClose, currentTheme, permanent, onOpenSettings }) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => {
      if (!permanent && !open) onClose();
    }}>
      <Dialog.Portal>
        <Dialog.Overlay forceMount asChild>
          <motion.div
            className={`modal-overlay theme-${currentTheme}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </Dialog.Overlay>
        <div className={`modal-content-wrapper theme-${currentTheme}`}>
          <Dialog.Content forceMount asChild>
            <motion.div
              className="modal-content migration-modal-content"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Dialog.Title className="sr-only">Migration to New Domain</Dialog.Title>
              <Dialog.Description className="sr-only">
                We have moved to kisekidle.app. Learn how to transfer your data.
              </Dialog.Description>

              {!permanent && (
                <Dialog.Close asChild>
                  <button className="modal-close">✖</button>
                </Dialog.Close>
              )}

              <div className="migration-modal-header">
                <span className="migration-modal-icon">🚀</span>
                <h2 className="mode-title" style={{ margin: 0 }}>We've Moved!</h2>
              </div>

              <div className="migration-modal-body">
                <p>
                  Kisekidle has a new home at
                  <a href="https://kisekidle.app" target="_blank" rel="noopener noreferrer" className="migration-link">
                    kisekidle.app
                  </a>.
                </p>

                <div className="migration-steps">
                  <h3>How to keep your progress:</h3>
                  <ol>
                    <li>
                      Click the gear icon (⚙️) on <strong>this</strong> site.
                    </li>
                    <li>
                      Click <strong>📥 Download Backup</strong> to save your data.
                    </li>
                    <li>
                      Go to <a href="https://kisekidle.app" target="_blank" rel="noopener noreferrer">kisekidle.app</a>.
                    </li>
                    <li>
                      Open Settings (⚙️) there and click <strong>📤 Import Backup</strong>.
                    </li>
                  </ol>
                </div>

                <div className="migration-warning">
                  ⚠️ This domain is no longer receiving updates. Please move to <strong>kisekidle.app</strong> to keep receiving updates!
                </div>
              </div>

              <button className="submit-button migration-go-btn" onClick={() => window.open("https://kisekidle.app", "_blank")}>
                Go to Kisekidle.app
              </button>

              {permanent ? (
                <button className="submit-button" onClick={onOpenSettings} style={{ marginTop: "10px" }}>
                  ⚙️ Open Settings (Download Backup)
                </button>
              ) : (
                <button className="migration-modal-dismiss" onClick={onClose}>
                  I'll do it later
                </button>
              )}
            </motion.div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
